---
title: Configuration
summary: The environment variables that matter, choosing a feature profile, and the first-run admin.
visibility: authenticated
audience: operator
section: Operating
order: 124
last_verified: 2026-10-25
owner: docs
---

# Configuration

The app is configured entirely through environment variables — the same image runs every
deployment. The backend reads from `.env` at the project root (or `backend/.env`).

## Key variables

| Variable | Notes |
|---|---|
| `SECRET_KEY` | **Required in production.** Signs JWTs. |
| `DATABASE_URL` | PostgreSQL in production; defaults to a local SQLite file in development. |
| `ENCRYPTION_KEY` | Fernet key used to encrypt stored credentials (auto-generated if unset). |
| `FEATURE_PROFILE` | Which profile to run: `full`, `smb`, `knowledge-ops`, `edge-local`, `minimal`. |
| `ENABLED_FEATURES` | Comma-separated delta on top of the profile (`name` adds, `-name` removes). |
| `RAG_PROXY_BASE` | Deployment-level RAG path the frontend and backend chat use (see [RAG routing](/help/operator/rag-routing)). |
| `RAG_PROXY_ORIGIN` | Where the backend sends that path. Leave it unset on AWS and Azure; set it everywhere else (see [RAG routing](/help/operator/rag-routing)). |
| `RAG_EXTERNAL_ALIAS_<NAME>` | Registers a cross-environment RAG alias. |
| `CORS_ORIGINS` | Comma-separated allowed origins. |
| `ACCESS_TOKEN_EXPIRE_MINUTES` / `REFRESH_TOKEN_EXPIRE_DAYS` | JWT lifetimes. |

## Choosing what's enabled

`FEATURE_PROFILE` picks a baseline; `ENABLED_FEATURES` adjusts it. Dependencies are validated
but not added for you, so include a feature's whole dependency chain. For exactly what each
profile turns on and how the delta works, see
[Feature profiles](/help/reference/feature-profiles).

## The first-run administrator

On first startup, if no admin exists, the backend seeds one administrator account and requires
its password to be changed on first login. The seed email and initial password are set by the
environment/seed configuration — change the password immediately, and don't reuse the default
beyond first login.

## Where credentials live

Third-party API keys (speech, avatars, payments, RAG) are encrypted at rest and managed in the
app, not in environment variables — see [Manage credentials](/help/how-to/manage-credentials).

## See also

- [Deploy the app](/help/operator/deploy)
- [Health and troubleshooting](/help/operator/health-and-troubleshooting)
